/**
 * Hex color code to RGBA array
 * @param hex string starting with # followed by 6 or 8 hex digits
 * @returns array of 3 or 4 numbers between 0 and 255
 */
export const hexColorToRgba = (hex: string): [number, number, number, number?] => {
  // Validate presence, length (7 or 9), prefix, and that all digits are valid hex
  const isValidHex = typeof hex === "string" && /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(hex);
  if (!isValidHex) {
    return [0, 0, 0];
  }

  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [0, 0, 0];
  }

  if (hex.length === 9) {
    const a = parseInt(hex.slice(7, 9), 16);
    if (Number.isNaN(a)) {
      return [0, 0, 0];
    }
    return [r, g, b, a];
  }

  return [r, g, b];
};

/**
 * RGBA to hex color code
 */
export const rgbaToHexColor = (rgba?: [number, number, number, number?], alpha?: number): string => {
  if (!rgba || rgba.length < 3) {
    return "";
  }

  const [r, g, b, a] = rgba;

  const alphaValue = typeof alpha === "number" ? alpha : (a ?? 255);

  return `#${formatNumberToCode(r)}${formatNumberToCode(g)}${formatNumberToCode(b)}${formatNumberToCode(alphaValue)}`;
};

const checkColorRange = (color: number): boolean => {
  if (!Number.isFinite(color) || color < 0 || color > 255) {
    return false;
  }
  return true;
};

/**
 * Convert array of [red, green, blue, alpha] to css rgba string
 * rgba(red, green, blue, alpha)
 * @param param0 array of 3 or 4 numbers between 0 and 255
 * @returns
 */
export const rgbaToCss = ([red, green, blue, alpha]: [number, number, number, number?]) => {
  if (!checkColorRange(red) || !checkColorRange(green) || !checkColorRange(blue)) {
    throw new TypeError("Expected three numbers between 0 and 255 got " + [red, green, blue]);
  }

  // Check if alpha is defined
  if (typeof alpha === "number") {
    if (!checkColorRange(alpha)) {
      throw new TypeError("Expected alpha number between 0 and 255, got " + alpha);
    }
    const alphaCss = `, ${(alpha / 255).toFixed(3)}`;

    return `rgba(${red}, ${green}, ${blue}${alphaCss})`;
  }

  return `rgb(${red}, ${green}, ${blue})`;
};

const formatNumberToCode = (input: number) => {
  return input.toString(16).padStart(2, "0");
};

/**
 * Blends an RGB color with a white background at a specified opacity level.
 *
 * This function calculates the resulting RGB values when a color with transparency
 * is rendered over a white background. The result is a solid (opaque) color that
 * appears identical to the semi-transparent color over white.
 *
 * Uses the alpha blending formula for each channel:
 * result = foreground × alpha + background × (1 - alpha)
 *
 * Where background is white (255, 255, 255)
 *
 * @param rgb - [red, green, blue] channel values (0-255)
 * @param alpha - The opacity level (0.0 to 1.0, where 0 is fully transparent and 1 is fully opaque)
 * @returns An array containing the blended RGB values
 *
 * @example
 * blendWithWhite([255, 0, 0], 0.25) // Returns [255, 191, 191] (red at 25% opacity)
 */
export const blendWithWhite = ([r, g, b]: [number, number, number], alpha: number): [number, number, number] => {
  const white = 255;

  return [
    Math.round(r * alpha + white * (1 - alpha)),
    Math.round(g * alpha + white * (1 - alpha)),
    Math.round(b * alpha + white * (1 - alpha)),
  ];
};

/**
 * Generates a consistent HEX color from any string input.
 *
 * This function uses a simple hash algorithm to convert a string into a number,
 * then derives RGB values from that hash. The same input string will always
 * produce the same color output.
 *
 * @param str - The input string to convert to a color
 * @returns A HEX color string (e.g., '#a3c2f1')
 *
 * @example
 * stringToColor('hello') // Always returns '#5eb363'
 * stringToColor('world') // Always returns '#8c52c5'
 */
export const stringToColor = (str: string): string => {
  if (!str) {
    return "#000000";
  }
  // Generate hash from string
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Extract RGB values from hash
  const r = (hash >> 16) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = hash & 0xff;

  // Convert to hex
  return rgbaToHexColor([r, g, b]);
};
